import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  CheckCircle,
  Error,
  ExpandLess,
  ExpandMore,
  Refresh,
} from "@material-ui/icons";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: theme.spacing(2),
    gap: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(2),
    overflowX: "auto",
  },
  filters: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "center",
  },
  chipSuccess: {
    background: "#e8f5e9",
    color: "#2e7d32",
    fontWeight: 600,
    fontSize: 11,
  },
  chipError: {
    background: "#ffebee",
    color: "#c62828",
    fontWeight: 600,
    fontSize: 11,
  },
  chipType: {
    background: "#e3f0fb",
    color: "#1565c0",
    fontSize: 11,
  },
  pre: {
    background: "#f5f5f5",
    borderRadius: 4,
    padding: theme.spacing(1),
    fontSize: 12,
    fontFamily: "monospace",
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
    maxHeight: 200,
    overflowY: "auto",
    margin: 0,
  },
  expandRow: {
    background: "#fafafa",
  },
}));

const NODE_TYPE_LABELS = {
  openai: "IA (OpenAI)",
  gemini: "IA (Gemini)",
  message: "Mensagem",
  input: "Input",
  condition: "Condição",
  menu: "Menu",
  httpRequest: "HTTP",
  variable: "Variável",
  tag: "Tag",
  ticket: "Departamento",
};

const LogRow = ({ log }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const hasError = !!log.error;

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen((v) => !v)}>
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </TableCell>
        <TableCell style={{ fontSize: 11, color: "#777" }}>
          {new Date(log.createdAt).toLocaleString("pt-BR")}
        </TableCell>
        <TableCell>
          <Chip
            label={NODE_TYPE_LABELS[log.nodeType] || log.nodeType}
            size="small"
            className={classes.chipType}
          />
        </TableCell>
        <TableCell style={{ fontSize: 12 }}>
          {log.nodeLabel || log.nodeId || "—"}
        </TableCell>
        <TableCell>
          {log.contact?.name || log.contact?.number || `#${log.contactId}` || "—"}
        </TableCell>
        <TableCell>
          {log.ticketId ? `#${log.ticketId}` : "—"}
        </TableCell>
        <TableCell>
          {log.durationMs != null ? `${log.durationMs}ms` : "—"}
        </TableCell>
        <TableCell align="center">
          {hasError ? (
            <Tooltip title={log.error}>
              <Chip
                icon={<Error style={{ fontSize: 14 }} />}
                label="Erro"
                size="small"
                className={classes.chipError}
              />
            </Tooltip>
          ) : (
            <Chip
              icon={<CheckCircle style={{ fontSize: 14 }} />}
              label="OK"
              size="small"
              className={classes.chipSuccess}
            />
          )}
        </TableCell>
      </TableRow>

      <TableRow className={classes.expandRow}>
        <TableCell colSpan={8} style={{ padding: 0 }}>
          <Collapse in={open} unmountOnExit>
            <div style={{ padding: "12px 24px", display: "flex", gap: 16, flexWrap: "wrap" }}>
              {log.input && (
                <div style={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="caption" color="textSecondary">
                    <strong>Entrada (mensagem do usuário)</strong>
                  </Typography>
                  <pre className={classes.pre}>{log.input}</pre>
                </div>
              )}
              {log.output && (
                <div style={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="caption" color="textSecondary">
                    <strong>Saída (resposta da IA)</strong>
                  </Typography>
                  <pre className={classes.pre}>{log.output}</pre>
                </div>
              )}
              {log.error && (
                <div style={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="caption" style={{ color: "#c62828" }}>
                    <strong>Erro</strong>
                  </Typography>
                  <pre className={classes.pre} style={{ background: "#ffebee" }}>
                    {log.error}
                  </pre>
                </div>
              )}
            </div>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const FlowLogs = () => {
  const classes = useStyles();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const [logs, setLogs] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [flowId, setFlowId] = useState(params.get("flowId") || "");
  const [ticketId, setTicketId] = useState(params.get("ticketId") || "");

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams();
      if (flowId) query.set("flowId", flowId);
      if (ticketId) query.set("ticketId", ticketId);
      query.set("limit", "100");

      const { data } = await api.get(`/flow-logs?${query.toString()}`);
      setLogs(data.logs);
      setCount(data.count);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line
  }, []);

  return (
    <div className={classes.root}>
      <MainHeader>
        <Title>Log de Execução de Fluxos</Title>
        <MainHeaderButtonsWrapper>
          <Tooltip title="Atualizar">
            <IconButton onClick={fetchLogs} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className={classes.filters}>
        <TextField
          label="Filtrar por ID do Fluxo"
          value={flowId}
          onChange={(e) => setFlowId(e.target.value)}
          variant="outlined"
          size="small"
          type="number"
          style={{ width: 200 }}
        />
        <TextField
          label="Filtrar por ID do Ticket"
          value={ticketId}
          onChange={(e) => setTicketId(e.target.value)}
          variant="outlined"
          size="small"
          type="number"
          style={{ width: 200 }}
        />
        <Chip
          label={`${count} registro${count !== 1 ? "s" : ""}`}
          color="primary"
          variant="outlined"
          size="small"
        />
      </div>

      <Paper className={classes.paper}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
            <CircularProgress />
          </div>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 40 }} />
                <TableCell>Data/Hora</TableCell>
                <TableCell>Tipo de Nó</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Contato</TableCell>
                <TableCell>Ticket</TableCell>
                <TableCell>Duração</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" style={{ color: "#aaa", padding: 32 }}>
                    Nenhum log encontrado. Execute um fluxo para ver os registros aqui.
                  </TableCell>
                </TableRow>
              )}
              {logs.map((log) => (
                <LogRow key={log.id} log={log} />
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </div>
  );
};

export default FlowLogs;
